/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOpenPopover';

const ActionOpenPopoverManager: ActionManagerFn<'ActionOpenPopover'> = (
  platform: Platform,
): ActionManager<'ActionOpenPopover'> => {
  return {
    ...baseActionManager(platform, 'ActionOpenPopover'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Popover Item ID',
        type: 'ItemID',
        initialFilter: 'Popover',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Popover') {
            return `Item id '${value}' doesn't belong to a Popover`;
          }
          return '';
        },
      },
    },
    replaceableWith: ['ActionClosePopover'],
    name: 'Open Popover',
    icon: { iconName: 'window-restore' },
    description: `**Overview**<br/>
The Open Popover action is a UI interaction component designed to programmatically activate a specific "Popover" component on the page. A Popover is a small, overlay window that typically appears relative to a specific element (like a button or icon), used for displaying context menus, additional information, or small forms without blocking the entire screen.<br/><br/>
This action functions as the trigger mechanism to reveal these overlays. It is tightly coupled with the Popover component type, employing strict validation to ensure that the target ID provided actually corresponds to a valid Popover, preventing runtime errors. It is designed to be easily swappable with the "Close Popover" action for quick logic adjustments.

**Key Features**<br/>
* **Targeted Activation:** Specifically designed to trigger the display logic of a Popover component.
* **Strict Type Validation:** Implements validateItemId to ensure the selected targetItemId is explicitly a Popover. If a different component type is selected (like a Drawer or Modal), the builder restricts the selection.
* **Registry Filtering:** The property picker uses initialFilter: 'Popover', streamlining configuration by only showing valid popover components in the dropdown list.
* **Component Interchangeability:** Explicitly configured in replaceableWith to be easily swapped with ActionClosePopover, allowing developers to toggle between opening and closing logic efficiently.

**Properties**<br/>
* **Popover Item ID (targetItemId):** A property of type ItemID.
  * *Function:* Identifies the unique ID of the Popover component to be opened.
  * *Validation:* Must reference a valid component of type 'Popover'.

**Use Cases**<br/>
* **Context Menus:** Clicking an "ellipsis" (...) icon in a grid row to open a Popover containing "Edit," "Delete," and "Share" options for that record.
* **Informational Tooltips:** Clicking a "Help" icon to open a Popover containing a detailed explanation of a complex metric.
* **Interactive Tours:** Programmatically opening a Popover pointing to a new feature when a user visits a page for the first time, guiding them through the interface.`,
  };
};

export default ActionOpenPopoverManager;
